import { useState } from "react";

import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  FormHelperText,
  TextField,
} from "@mui/material";

import { Example } from "@/types";
import { isHtmlContentEmpty } from "@/utils/htmlContentStyle";

import { EditingField } from "../../tutor/Tabs/Problems/features";

interface ValidationErrors {
  concept?: string;
  description?: string;
}

interface Props {
  example: Example;
  onUpdate: (example: Example) => void;
  onCancelEdit: () => void;
}

const EditExample = ({ example, onUpdate, onCancelEdit }: Props) => {
  const [editedExample, setEditedExample] = useState<Example>(example);
  const [errors, setErrors] = useState<ValidationErrors>({});

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    if (!editedExample.concept?.trim()) {
      newErrors.concept = "Concept is required";
      isValid = false;
    }

    if (isHtmlContentEmpty(editedExample.description)) {
      newErrors.description = "Description is required";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleChange = (field: keyof Example, value: Example[keyof Example]) => {
    setEditedExample((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for the field being changed
    if (errors[field as keyof ValidationErrors]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const handleSave = () => {
    if (validateForm()) {
      onUpdate(editedExample);
    }
  };

  return (
    <>
      <DialogContent
        sx={{
          px: 3,
          py: 2.5,
        }}
      >
        <Box>
          <TextField
            label="Concept"
            fullWidth
            value={editedExample.concept}
            onChange={(e) => handleChange("concept", e.target.value)}
            error={!!errors.concept}
            helperText={errors.concept}
            sx={{ mb: 2 }}
          />
          <EditingField
            label="Description"
            value={editedExample.description}
            onChange={(value) => handleChange("description", value)}
            type="textarea"
            placeholder="Enter the description"
            isEditing={true}
            initialHeight={400}
            error={!!errors.description}
          />
          {errors.description && (
            <FormHelperText error>{errors.description}</FormHelperText>
          )}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          py: 1.25,
          px: 3,
          borderTop: "1px solid",
          borderColor: "rgba(28, 72, 46, 0.45)",
          justifyContent: "flex-end",
          gap: 1,
        }}
      >
        <Button variant="contained" color="primary" onClick={handleSave} size="small">
          Save
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          onClick={onCancelEdit}
          size="small"
        >
          Cancel
        </Button>
      </DialogActions>
    </>
  );
};

export default EditExample;
